import { createApp, type App } from 'vue';
import AppEl from '../App.vue';
import { createPinia } from 'pinia';
import { version } from '../../package.json';
import {
    ArboTabs,
    ArboToggle,
    ArboInput,
    ArboIcon,
    ArboButton,
    ArboTooltip,
    ArboThumbnail,
    TessAvatar,
    TessBreadcrumb,
    TessAvatarGroup,
    TessMoreActions,
    TessHoverTile,
    tooltip,
    TessTimeTag,
    autoFocus
} from '@arbocollab/arbo-ui-components';
import { getWS_Code } from '@/utils';
import { setupErrorHelper } from './errorUtilities';

/**
 * Collection of UI components to be registered globally in the Vue application
 */
const components = {
    ArboTabs,
    ArboToggle,
    ArboInput,
    ArboIcon,
    ArboButton,
    ArboTooltip,
    ArboThumbnail,
    TessAvatar,
    TessBreadcrumb,
    TessAvatarGroup,
    TessMoreActions,
    TessHoverTile,
    TessTimeTag
};

/**
 * Initializes the global search Vue application with all necessary plugins and components
 *
 * @returns {App<Element>} The configured Vue application instance
 *
 * @example
 * ```typescript
 * const app = initGlobalSearch();
 * app.mount('#app');
 * ```
 */
export const initGlobalSearch = async () => {
    console.log('Initializing global search...', version);
    const appInstance: App<Element> = createApp(AppEl);

    appInstance.provide('switchInstance', appInstance.config.globalProperties.switchInstance);
    const pinia = createPinia();
    const i18n = await setupErrorHelper();

    // Register all UI components globally
    Object.entries(components).forEach(([name, component]) => {
        appInstance.component(name, component);
    });

    // Register custom directives
    appInstance.directive('arbo-tooltip', tooltip);
    appInstance.directive('auto-focus', autoFocus);
    appInstance.use(i18n);
    console.log('Global search initialized successfully', i18n);
    // Global error handler for better debugging
    appInstance.config.errorHandler = (err: any, vm: any, info: any) => {
        console.error('Error:', err);
        console.error('Vue component:', vm);
        console.error('Additional info:', info);
    };

    appInstance.use(pinia);

    return appInstance;
};

/**
 * Returns the base link configuration object containing domain URLs and workspace code
 *
 * @returns {Object} Configuration object with base domains and workspace code
 * @returns {string} returns.baseDomain - The base domain URL from environment variables
 * @returns {string} returns.reviewDomain - The review domain URL from environment variables
 * @returns {string} returns.ws_code - The workspace code retrieved from utils
 *
 * @example
 * ```typescript
 * const config = baseLinkConfig();
 * console.log(config.baseDomain); // "https://example.com"
 * ```
 */
export const baseLinkConfig = () => {
    return {
        baseDomain: sessionStorage.getItem('BASE_URL') || '',
        reviewDomain: sessionStorage.getItem('BASE_REVIEW_URL') || '',
        ws_code: getWS_Code()
    };
};

/**
 * Checks if the Ctrl key (Windows/Linux) or Cmd key (Mac) is pressed during an event
 *
 * @param {MouseEvent | KeyboardEvent} event - The mouse or keyboard event to check
 * @returns {boolean} True if Ctrl or Cmd key is pressed, false otherwise
 *
 * @example
 * ```typescript
 * const handleClick = (event: MouseEvent) => {
 *   if (isCtrlOrCmdClick(event)) {
 *     // Handle Ctrl/Cmd + click
 *   }
 * };
 * ```
 */
export const isCtrlOrCmdClick = (event: MouseEvent | KeyboardEvent): boolean => {
    return event.ctrlKey || event.metaKey;
};

/**
 * Handles click actions for navigation links with support for new tab opening
 *
 * @param {MouseEvent} event - The mouse click event
 * @param {string} link - The URL to navigate to
 * @param {Record<any, any>} [params] - Optional query parameters to append to the URL
 *
 * @description
 * - If Ctrl/Cmd is held during click, opens link in new tab
 * - Otherwise, navigates to the link in the current window
 * - Automatically appends query parameters if provided
 *
 * @example
 * ```typescript
 * const handleLinkClick = (event: MouseEvent) => {
 *   clickActionHandler(event, '/search', { query: 'test', filter: 'active' });
 * };
 * ```
 */
export const clickActionHandler = (event: MouseEvent, link: string, params?: Record<any, any>, callbackCmd?: any) => {
    const hasParams = Boolean(params && Object.keys(params).length > 0);

    event.preventDefault();
    if (isCtrlOrCmdClick(event)) {
        // open in new tab
        window.open(link, '_blank');
    } else {
        const searchParams = new URLSearchParams(params).toString();
        const linkWithParams = hasParams ? `${link}${link.includes('?') ? '&' : '?'}${searchParams}` : link;
        // Only use pushState for same-origin relative URLs
        try {
            if (callbackCmd && typeof callbackCmd === 'function') {
                callbackCmd();
            } else {
                window.location.href = linkWithParams;
            }
        } catch (e: any) {
            console.error('Error replacing URL:', e);
            // fallback in case of invalid URL
            // window.location.href = linkWithParams;
        }
    }
};

/**
 * Handles keyboard press actions for navigation links with support for new tab opening
 *
 * @param {KeyboardEvent | MouseEvent} event - The keyboard or mouse event
 * @param {string} link - The URL to navigate to
 * @param {Record<any, any>} [params] - Optional query parameters to append to the URL
 *
 * @description
 * Similar to clickActionHandler but designed for keyboard interactions (Enter key, etc.)
 * - If Ctrl/Cmd is held during press, opens link in new tab
 * - Otherwise, navigates to the link in the current window
 * - Automatically appends query parameters if provided
 *
 * @example
 * ```typescript
 * const handleKeyPress = (event: KeyboardEvent) => {
 *   if (event.key === 'Enter') {
 *     pressActionHandler(event, '/dashboard', { tab: 'overview' });
 *   }
 * };
 * ```
 */
export const pressActionHandler = (event: KeyboardEvent | MouseEvent, link: string, params?: Record<any, any>) => {
    event.preventDefault();
    if (isCtrlOrCmdClick(event)) {
        // open in new tab
        window.open(link, '_blank');
    } else {
        const searchParams = new URLSearchParams(params).toString();
        const linkWithParams = `${link}${link.includes('?') ? '&' : '?'}${searchParams}`;
        // Only use pushState for same-origin relative URLs
        try {
            window.location.href = linkWithParams;
        } catch (e: any) {
            console.error('Error replacing URL:', e);
            // fallback in case of invalid URL
            // window.location.href = linkWithParams;
        }
    }
};
